<template>
  <div class="toolbar-header">
    <div class="toolbar-header-left">
      <h2 class="header-title">
        {{ headerText }}
      </h2>
      <div class="mobile-actions">
        <a :href="downloadAllEvents">
          <DownloadIcon />
          <span class="sr-only">{{ $t('events.downloadCalendar') }}</span>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useNuxtApp, useRuntimeConfig } from '#imports';
import DownloadIcon from './icons/DownloadIcon.vue';
import { useEventsAnnouncer } from '../../../composables/useEventsAnnouncer.js';
import { useEventsStore } from '../../../stores/eventsStore.js';
import { useMainStore } from '../../../stores/mainStore.js';

defineOptions({ name: 'EventsToolbarHeader' });

const eventsStore = useEventsStore();
const mainStore = useMainStore();
const { $t } = useNuxtApp();
const runtimeConfig = useRuntimeConfig();
const { announce } = useEventsAnnouncer();

const searchText = computed(() => eventsStore.searchText);
const queryParams = computed(() => eventsStore.queryParams);
const allEvents = computed(() => eventsStore.allEvents);

const headerText = computed(() => {
  if (searchText.value) {
    return allEvents.value.length > 0
      ? $t('events.resultsForSearch', { searchQuery: searchText.value })
      : $t('events.noResultsForSearch', { searchQuery: searchText.value });
  }
  return $t('events.title');
});

const isLoading = computed(() => eventsStore.isLoading);
const settledSearchText = ref(null);
watch(
  isLoading,
  (loading) => {
    if (!loading) settledSearchText.value = searchText.value;
  },
  { immediate: true },
);

const noResultsMessage = computed(() =>
  !isLoading.value && searchText.value === settledSearchText.value && searchText.value && allEvents.value.length === 0
    ? $t('events.noResultsForSearch', { searchQuery: searchText.value })
    : '',
);

onMounted(() => announce(noResultsMessage.value));
watch(noResultsMessage, (message) => announce(message));

const downloadAllEvents = computed(() => {
  const { section_ids = '', filter_ids = '' } = queryParams.value;
  const { url } = mainStore.getEventsApi(runtimeConfig.public.AXIOS_BROWSER_BASE_API_URL);
  return `${url}/generate_ical?filter_ids${filter_ids ? '=' : ''}${filter_ids}&section_ids${
    section_ids ? '=' : ''
  }${section_ids}`;
});
</script>

<style scoped lang="scss">
@use '../../global/scss/mixins/breakpoints.scss';

@include breakpoints.screen('>=xs') {
  .sr-only {
    border: 0;
    clip: rect(0, 0, 0, 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }
  .toolbar-header {
    margin-top: 32px;
    .toolbar-header-left,
    .toolbar-header-right {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
    }
  }
  .header-title {
    font-style: normal;
    font-weight: 900;
    font-size: 32px;
    line-height: 32px;
    letter-spacing: 1.5px;
    margin: 0 8px 0 0;
    font-family: inherit;
    color: inherit;
  }
  .mobile-actions {
    display: flex;
    align-items: center;
    margin-left: 5px;
    button {
      background: none;
      border: none;
      &:hover {
        cursor: pointer;
      }
    }
  }
}
@include breakpoints.screen('xs') {
  .toolbar-header {
    .toolbar-header-left {
      .header-title {
        margin-right: 0;
      }
      .view-header {
        margin-right: 0;
      }
    }
  }
}
</style>
